import { UnbHeightPx, SampleNavHeightPx } from 'constants/navBar';
import React from 'react';
import {
    Group,
    Box,
    Heading,
    Paragraph,
    useBreakpoint,
    Button,
    Logo,
    Container,
    ContainerProps,
} from '@landr/maestro';
import { useAppConfig } from '@landr/mfe';
import { I18nProvider } from '@lingui/react';
import { i18n } from 'utils/setupTranslations';
import AppStyleProvider from 'AppGlobalStyle';
import { Trans } from '@lingui/macro';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { css } from '@landr/styled-system/css';
import { getSupportLink, Languages } from '@landr/core';

const errorPageBody = css({
    position: 'relative',
    marginLeft: 'xl',
    marginRight: 'xl',
});

const linkStyle = css({
    textDecoration: 'underline',
});

const refreshPage = () => {
    window?.location?.reload();
};

interface PageUnknownErrorProps {
    isGlobal?: boolean;
    status?: number;
}

// `PageUnknownError` wrap VerboseError component in a page layout.
// This is what is returned by `withErrorBoundary(Component, 'page');`
// It should only be use for pages or components that include page layout structure.
// If your component do not have page layout structure, use `<VerboseError />` directly
// For more info consult error/README.md
const PageUnknownError: React.FC<PageUnknownErrorProps> = ({
    isGlobal = false,
    status,
}) => {
    const isMd = useBreakpoint('md');

    let size: ContainerProps['size'];
    let title: JSX.Element;
    let description: JSX.Element;

    const { links } = useAppConfig();
    const origin = links.supportLink.originUrl;
    const supportLink = getSupportLink({
        language: i18n.locale as Languages,
        pathname: '/requests/new',
        origin,
    });

    switch (status) {
        case 423:
        case 503:
            size = 'md';
            title = <Trans>Content temporary unavailable</Trans>;
            description = (
                <Trans>
                    Refresh the page to try again. If the problem persists{' '}
                    <a className={linkStyle} href={supportLink} target="_blank">
                        contact support
                    </a>{' '}
                    for more help.
                </Trans>
            );
            break;

        default:
            size = 'ms';
            title = <Trans>Something wasn't plugged right.</Trans>;
            description = (
                <Trans>
                    We couldn't find what you were looking for. If the problem
                    persists, check that your browser is up-to-date or{' '}
                    <a
                        className={linkStyle}
                        href="https://support.landr.com/hc/en-us/requests/new"
                        target="_blank"
                    >
                        contact support
                    </a>{' '}
                    for more help.
                </Trans>
            );
    }

    return (
        <>
            {isGlobal && (
                <HelmetProvider>
                    <Helmet>
                        <meta name="prerender-status-code" content="500" />
                    </Helmet>
                </HelmetProvider>
            )}
            <I18nProvider i18n={i18n}>
                <AppStyleProvider>
                    <Box
                        className={errorPageBody}
                        style={{
                            height: `calc(100vh - (${UnbHeightPx}px + ${SampleNavHeightPx}px))`,
                        }}
                    >
                        <Group
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <Container size={size}>
                                <Group
                                    direction="vertical"
                                    alignItems="center"
                                    textAlign="center"
                                    py="jb"
                                >
                                    {isGlobal && (
                                        <Box mb="xl">
                                            <Logo variant="brand" />
                                        </Box>
                                    )}
                                    <Heading
                                        size={isMd ? 'xxxl' : 'mg'}
                                        data-testid="big-title"
                                        mb="md"
                                        as="h1"
                                    >
                                        {title}
                                    </Heading>
                                    <Paragraph
                                        color="text.medium"
                                        textStyle="body"
                                        mb="xxl"
                                    >
                                        {description}
                                    </Paragraph>
                                    {!status ? (
                                        <Button
                                            variant="secondary"
                                            isRounded
                                            as="a"
                                            href="/"
                                        >
                                            <Trans>Back to Samples</Trans>
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="secondary"
                                            isRounded
                                            onClick={refreshPage}
                                        >
                                            <Trans>Refresh</Trans>
                                        </Button>
                                    )}
                                </Group>
                            </Container>
                        </Group>
                    </Box>
                </AppStyleProvider>
            </I18nProvider>
        </>
    );
};

export default PageUnknownError;
